import { useAtom } from '@reatom/npm-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Skeleton, Typography } from '@blade/ui-kit'
import { Numeric } from '@blade/ui-kit'
import { nativeNetworkCurrencyAtom } from '@root/entities/currency'

import { GasSettingsDrawer } from '../gas-settings-drawer/gas-settings-drawer'

import { TransferFeeEditButton } from './transfer-fee-edit-button'

import type { TransferGasSettingsReatom } from '../../../atoms/transfer-fee-reatom'
import type { ClassNameProps } from '@root/shared/react'

type TransferFeeCardProps = {
  model: TransferGasSettingsReatom
  variant?: 'dark' | 'normal'
} & ClassNameProps

export const TransferFeeCardEvm: React.FC<TransferFeeCardProps> = ({
  model,
  className,
  variant = 'normal',
}) => {
  const { t } = useTranslation()

  const [settingsSelection] = useAtom(model.selectedGasSettings)
  const [nativeCurrency] = useAtom(nativeNetworkCurrencyAtom)

  const [estimatedTransferFeeState] = useAtom(
    model.estimatedTransferGasFeeEthStateAtom,
  )
  const [estimatedTransferMaxGasFeeEthState] = useAtom(
    model.estimatedTransferMaxGasFeeEthStateAtom,
  )
  const [estimatedGasFeeUsd] = useAtom(model.estimatedGasFeeUsdAtom)
  const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false)

  const renderEstimatedFee = () => {
    if (!AsyncStateN.isSuccess(estimatedTransferFeeState)) {
      return <Skeleton height={16} width={60} paddingBlock={2} />
    }

    return (
      <FeeValue>
        <Numeric
          value={estimatedTransferFeeState.value}
          preset="currency-amount-short"
          currency={nativeCurrency.ticker}
        />
      </FeeValue>
    )
  }

  const renderMaxFee = () => {
    if (!AsyncStateN.isSuccess(estimatedTransferMaxGasFeeEthState)) {
      return <Skeleton height={12} width={50} />
    }

    return (
      <MaxFeeValue>
        <Numeric
          value={estimatedTransferMaxGasFeeEthState.value}
          preset="currency-amount-short"
          currency={nativeCurrency.ticker}
        />
      </MaxFeeValue>
    )
  }

  return (
    <Root className={className} $variant={variant}>
      <MainContent>
        <EstimatedFeeContainer>
          <FeeLabel>{t('gas-settings.estimated-fee')}:</FeeLabel>
          {renderEstimatedFee()}
        </EstimatedFeeContainer>

        <MaxFee>
          <MaxFeeLabel>{t('gas-settings.max-fee')}:</MaxFeeLabel>
          {renderMaxFee()}
        </MaxFee>
      </MainContent>
      <TransferFeeEditButton
        settingsSelection={settingsSelection}
        estimatedGasFeeUsd={estimatedGasFeeUsd}
        onEdit={() => {
          setSettingsDrawerOpen(true)
        }}
      />

      <GasSettingsDrawer
        open={settingsDrawerOpen}
        onOpenChange={setSettingsDrawerOpen}
        gasSettingsModel={model}
      />
    </Root>
  )
}

const Root = styled.div<{ $variant: 'normal' | 'dark' }>`
  padding-block: 6px;
  padding-inline: 14px 6px;

  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;

  border-radius: 16px;
  border: 1px solid
    ${({ $variant }) =>
      $variant === 'dark'
        ? `rgba(var(--background-1-rgb), 0.05)`
        : 'var(--backgroundA-2)'};
  background: ${({ $variant }) =>
    $variant === 'dark' ? 'var(--background-1)' : 'var(--backgroundA-3)'};
`

const EstimatedFeeContainer = styled.div`
  display: grid;
  justify-items: start;
  align-items: center;
`

const MainContent = styled.div`
  display: grid;
  align-items: center;
  grid-template-rows: auto auto;
  grid-gap: 2px;
`

const FeeLabel = styled(Typography).attrs({
  variant: 'body4-regular',
  component: 'div',
})`
  color: var(--text-12);
`

const FeeValue = styled(Typography).attrs({
  variant: 'body4-semi-bold',
  component: 'div',
})`
  color: var(--text-12);
`

const MaxFee = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  justify-content: start;
  justify-items: start;
  gap: 2px;
`

const MaxFeeLabel = styled(Typography).attrs({
  variant: 'body5-bold',
  component: 'div',
})`
  color: var(--text-8);
`

const MaxFeeValue = styled(Typography).attrs({
  variant: 'body5-medium',
  component: 'div',
})`
  color: var(--text-8);
`
